import React, { useMemo, useState, useEffect } from "react";
import { Card, CardContent } from "@/designs/shadcn/ui/card";
import PaymentOptionSelect from "@/shared/ui/components/payment-option-select";
import Badge from "@/shared/ui/components/badge";
import { useTranslation } from "react-i18next";
import MoneyAmount from "@/shared/ui/components/money-amount";
import {
  type AppContext,
  Currency,
  PaymentOptionTrait,
  type TranslateFunc,
} from "@/types";
import {
  useCheckoutSessionV3,
  useModalsActions,
  usePaymentPlansActions,
  useSelectedPaymentOption,
} from "@/modules/checkout-session";
import { formatWithComponents } from "@/lib/common";
import {
  buildSubventionDataMap,
  getSubventionBannerMessageKey,
  getSubventionEligibleTenures,
  isSubventionEligible,
  shouldSuppressFeeLineForSubventionBanner,
} from "@/lib/subvention";
import * as events from "@/events";
import { logInfo } from "@/services/logging/logger";
import {
  emitPaymentPlanTracking,
  emitPaymentDetailsBottomSheetTracking,
} from "@/shared/tracking/analytics";
import { PercentageWhiteCircleIcon } from "@/shared/ui/icons/percentage-white-circle-icon";
import { IneligibleLock } from "@/shared/ui/icons/ineligible-lock";
import { SparkleIcon } from "@/shared/ui/icons/sparkle-icon";
import { usePriceCalloutGate } from "@/shared/abt";

// =============================================================================
// Types & Interfaces
// =============================================================================

interface InstalmentOptionPickerProps {
  chevronIcon: React.ReactNode;
  ctx: AppContext;
  disabled?: boolean;
}

interface PaymentHeaderProps {
  hasAdjustedDownPayment: boolean;
  downPaymentAmount: number;
  monthlyAmount: number;
  currency: Currency;
  language: string;
  selectedPayments: number;
  isDeclinedOfferSelected: boolean;
  t: TranslateFunc;
}

interface FeeDisplayProps {
  hasIncludesFees: boolean;
  feeAmount: number;
  averageMonthlyFee?: { amount: number; currency: string };
  currency: Currency;
  language: string;
  t: TranslateFunc;
}

interface SeeDetailsButtonProps {
  disabled: boolean;
  onClick: () => void;
  chevronIcon: React.ReactNode;
  t: TranslateFunc;
}

interface UnequalPlanBannerProps {
  downPaymentAmount: number;
  currency: Currency;
  language: string;
  t: TranslateFunc;
  containerClassName?: string;
}

interface UnequalPlanBannerWithFeeProps extends UnequalPlanBannerProps {
  averageMonthlyFee?: { amount: number; currency: string };
}

// =============================================================================
// Custom Hooks
// =============================================================================

function usePaymentOptions(
  session: ReturnType<typeof useCheckoutSessionV3>["data"],
) {
  return useMemo((): Array<{
    payments: number;
    noFees: boolean;
    isDeclined?: boolean;
  }> => {
    const baseOptions = (session?.paymentOptions ?? [])
      .filter((option) => option.tenure >= 2)
      .map((option) => ({
        payments: option.tenure,
        noFees: option.totalCustomerFeeAmount.amount === 0,
      }));

    const baseTenures = new Set(baseOptions.map((o) => o.payments));

    const declinedOnlyOptions = (session?.declinedOffers ?? [])
      .filter((d) => d.subvention != null)
      .map((d) => ({
        payments: Number(d.tenure ?? 0),
        noFees: false,
        isDeclined: true as const,
      }))
      .filter((o) => o.payments >= 2 && !baseTenures.has(o.payments));

    return [...baseOptions, ...declinedOnlyOptions].sort(
      (a, b) => a.payments - b.payments,
    );
  }, [session]);
}

function useSubventionData(
  session: ReturnType<typeof useCheckoutSessionV3>["data"],
) {
  return useMemo(
    () => buildSubventionDataMap(session?.paymentOptions ?? []),
    [session?.paymentOptions],
  );
}

function useWaivedOffFeeData(
  session: ReturnType<typeof useCheckoutSessionV3>["data"],
) {
  return useMemo(
    () =>
      (session?.paymentOptions ?? []).reduce<
        Record<number, { amount: number }>
      >((acc, option) => {
        if (option.waivedOffFee?.amount) {
          acc[option.tenure] = { amount: option.waivedOffFee.amount };
        }
        return acc;
      }, {}),
    [session],
  );
}

function useSelectedOption(
  session: ReturnType<typeof useCheckoutSessionV3>["data"],
  selectedPaymentOption: ReturnType<typeof useSelectedPaymentOption>,
  selectedPaymentsForDisplay: number,
) {
  return useMemo(() => {
    const tenureToFind =
      selectedPaymentOption?.type === "PAY_BY_INSTALMENTS" &&
      selectedPaymentOption.payments >= 2
        ? selectedPaymentOption.payments
        : selectedPaymentsForDisplay;

    return session?.paymentOptions.find(
      (option) => option.tenure === tenureToFind,
    );
  }, [session, selectedPaymentOption, selectedPaymentsForDisplay]);
}

// =============================================================================
// Sub-components
// =============================================================================

function PaymentHeader({
  hasAdjustedDownPayment,
  downPaymentAmount,
  monthlyAmount,
  currency,
  language,
  selectedPayments,
  isDeclinedOfferSelected,
  t,
}: PaymentHeaderProps) {
  const todayKey = "plan_page_improvements.common.today";
  const thenKey = "plan_page_improvements.common.then";
  const perMonthKey = "plan_page_improvements.common.per_month";

  // Arabic grammar: for 11–99 the counted noun is singular (دفعة), for 3–10 it's plural (دفعات)
  const shouldUseArabicSingularRepaymentLabel =
    language.startsWith("ar") && selectedPayments >= 11;
  const paymentsLabelKey = shouldUseArabicSingularRepaymentLabel
    ? "common.payments_one"
    : "common.payments";

  return (
    <div className="flex items-start justify-between mb-1 px-6 pt-6">
      <div>
        {isDeclinedOfferSelected ? (
          <div className="text-[18px] font-semibold leading-[24px] tracking-[-0.27px] text-[#7E82AD]">
            {t("plan.not_available")}
          </div>
        ) : hasAdjustedDownPayment ? (
          <div className="text-[18px] font-semibold text-gray-900">
            <MoneyAmount
              amount={downPaymentAmount}
              currency={currency}
              className="inline-flex"
              language={language}
            />
            <span className="font-semibold"> {t(todayKey)}</span>
            <div className="text-[16px] font-medium text-[#585B7A] mt-1">
              {t(thenKey)}{" "}
              <MoneyAmount
                amount={monthlyAmount}
                currency={currency}
                className="inline-flex"
                language={language}
              />
              {t(perMonthKey)}
            </div>
          </div>
        ) : (
          <div className="text-[18px] font-semibold text-gray-900">
            <MoneyAmount
              amount={monthlyAmount}
              currency={currency}
              className="inline-flex"
              language={language}
            />
            {t(perMonthKey)}
          </div>
        )}
      </div>
      <Badge
        size="medium"
        leftContent={
          isDeclinedOfferSelected ? (
            <IneligibleLock
              className="w-4 h-4 shrink-0"
              fill="white"
              aria-hidden
              data-testid="ineligible-lock-badge"
            />
          ) : undefined
        }
      >
        {selectedPayments} {t(paymentsLabelKey, { count: selectedPayments })}
      </Badge>
    </div>
  );
}

function FeeDisplay({
  hasIncludesFees,
  feeAmount,
  averageMonthlyFee,
  currency,
  language,
  t,
}: FeeDisplayProps) {
  const noFeesKey = "plan_page_improvements.plan.no_fees";
  const feeKey = averageMonthlyFee?.amount
    ? "plan.includes_monthly_fee"
    : "plan.includes_fee";
  const displayAmount = averageMonthlyFee?.amount ?? feeAmount ?? 0;

  return (
    <div
      className={`px-6 pb-3 ${hasIncludesFees ? "text-[#585B7A]" : "text-[#128351]"}`}
    >
      {hasIncludesFees
        ? formatWithComponents(
            t(feeKey),
            {
              amount: (
                <MoneyAmount
                  amount={displayAmount}
                  currency={currency}
                  className="inline-flex mx-0.5"
                  language={language}
                />
              ),
            },
            (text) => (
              <span className="mr-0.5 rtl:mr-0 rtl:ml-0.5">{text}</span>
            ),
          )
        : t(noFeesKey)}
    </div>
  );
}

function SeeDetailsButton({
  disabled,
  onClick,
  chevronIcon,
  t,
}: SeeDetailsButtonProps) {
  const seeDetailsKey = "plan_page_improvements.plan.see_details";
  return (
    <div className="border-t-[0.5px] border-[#DAE0EB]">
      <button
        onClick={onClick}
        disabled={disabled}
        className={`w-full text-left px-6 py-4 transition-colors focus:outline-hidden ${disabled ? "cursor-not-allowed" : "cursor-pointer"}`}
      >
        <div className="flex pb-1 items-center justify-between">
          <span
            className={`font-medium ${disabled ? "text-[#585B7A]" : "text-[#16181D]"}`}
          >
            {t(seeDetailsKey)}
          </span>
          <span
            className={
              disabled
                ? "[&_path]:fill-[#585B7A] [&_path]:stroke-[#585B7A]"
                : ""
            }
            data-testid={disabled ? "plan-details-chevron-disabled" : undefined}
          >
            {chevronIcon}
          </span>
        </div>
      </button>
    </div>
  );
}

export function UnequalPlanBanner({
  downPaymentAmount,
  currency,
  language,
  t,
  containerClassName = "mx-6 mb-4",
}: UnequalPlanBannerProps) {
  return (
    <div
      className={`${containerClassName} px-4 py-3 rounded-[12px] bg-[#EBEEF4]`}
      data-testid="unequal-plan-banner"
    >
      <div className="text-xs font-medium text-[#585B7A]">
        {formatWithComponents(
          t("plan.unequal_banner"),
          {
            amount: (
              <MoneyAmount
                amount={downPaymentAmount}
                currency={currency}
                className="inline-flex"
                gapClassName="gap-0.5"
                language={language}
              />
            ),
          },
          (text) => (
            <span className="mr-0.5 rtl:mr-0 rtl:ml-0.5">{text}</span>
          ),
        )}
      </div>
    </div>
  );
}

export function UnequalPlanBannerWithFee({
  downPaymentAmount,
  currency,
  language,
  t,
  averageMonthlyFee,
  containerClassName = "mx-6 mb-4",
}: UnequalPlanBannerWithFeeProps) {
  const averageMonthlyFeeAmount = averageMonthlyFee?.amount ?? 0;
  return (
    <div
      className={`${containerClassName} px-4 py-3 rounded-[12px] bg-[#EBEEF4]`}
      data-testid="unequal-plan-banner-with-fee"
    >
      <div className="text-xs font-medium text-[#585B7A]">
        {formatWithComponents(
          t("plan.unequal_banner_with_fees"),
          {
            downPayment: (
              <MoneyAmount
                amount={downPaymentAmount}
                currency={currency}
                className="inline-flex"
                gapClassName="gap-0.5"
                language={language}
              />
            ),
            averageMonthlyFees: (
              <MoneyAmount
                amount={averageMonthlyFeeAmount}
                currency={currency}
                className="inline-flex"
                gapClassName="gap-0.5"
                language={language}
              />
            ),
          },
          (text) => (
            <span className="mr-0.5 rtl:mr-0 rtl:ml-0.5">{text}</span>
          ),
        )}
      </div>
    </div>
  );
}

// =============================================================================
// Main Component
// =============================================================================

export function InstalmentOptionPicker({
  chevronIcon,
  ctx,
  disabled = false,
}: InstalmentOptionPickerProps) {
  const { t, i18n } = useTranslation();
  const priceCalloutGate = usePriceCalloutGate();
  const { data: session } = useCheckoutSessionV3();
  const selectedPaymentOption = useSelectedPaymentOption();
  const { setIsOrderDetailsOpen } = useModalsActions();
  const { setSelectedPaymentOption } = usePaymentPlansActions();
  const [lastValidInstallmentSelection, setLastValidInstallmentSelection] =
    useState<number | null>(null);

  // Computed data
  const paymentOptions = usePaymentOptions(session);
  const subventionData = useSubventionData(session);
  const waivedOffFeeData = useWaivedOffFeeData(session);

  useEffect(() => {
    if (
      selectedPaymentOption?.type === "PAY_BY_INSTALMENTS" &&
      selectedPaymentOption.payments >= 2
    ) {
      const option = paymentOptions.find(
        (opt) => opt.payments === selectedPaymentOption.payments,
      );
      if (option) {
        // eslint-disable-next-line react-hooks/set-state-in-effect
        setLastValidInstallmentSelection(selectedPaymentOption.payments);
      }
    }
  }, [selectedPaymentOption, paymentOptions]);

  useEffect(() => {
    if (lastValidInstallmentSelection === null && paymentOptions.length > 0) {
      const preSelectedInstalment = session?.paymentOptions?.find(
        (opt) => opt.tenure >= 2 && opt.preSelected === true,
      );
      if (preSelectedInstalment) {
        // eslint-disable-next-line react-hooks/set-state-in-effect
        setLastValidInstallmentSelection(preSelectedInstalment.tenure);
      } else if (paymentOptions[0]?.payments) {
        setLastValidInstallmentSelection(paymentOptions[0].payments);
      }
    }
  }, [lastValidInstallmentSelection, paymentOptions, session]);

  const selectedPaymentsForDisplay = useMemo(() => {
    if (selectedPaymentOption && selectedPaymentOption.payments >= 2) {
      const option = paymentOptions.find(
        (opt) => opt.payments === selectedPaymentOption.payments,
      );
      if (option) return option.payments;
    }

    if (lastValidInstallmentSelection !== null) {
      const option = paymentOptions.find(
        (opt) => opt.payments === lastValidInstallmentSelection,
      );
      if (option) return lastValidInstallmentSelection;
    }

    const preSelectedInstalment = session?.paymentOptions?.find(
      (opt) => opt.tenure >= 2 && opt.preSelected === true,
    );
    if (preSelectedInstalment) {
      return preSelectedInstalment.tenure;
    }

    return paymentOptions[0]?.payments ?? 0;
  }, [
    selectedPaymentOption,
    paymentOptions,
    session,
    lastValidInstallmentSelection,
  ]);

  const selectedOption = useSelectedOption(
    session,
    selectedPaymentOption,
    selectedPaymentsForDisplay,
  );

  const monthlyAmount = useMemo(() => {
    return (
      session?.paymentOptions.find(
        (option) => option.tenure === selectedPaymentsForDisplay,
      )?.instalmentAmount.amount ?? 0
    );
  }, [session, selectedPaymentsForDisplay]);

  const selectedIsSubventionEligible = Boolean(
    selectedOption && isSubventionEligible(selectedOption),
  );
  const subventionBannerMessageKey = selectedOption
    ? getSubventionBannerMessageKey(
        selectedOption,
        session?.order?.countryCode,
      )
    : null;
  const hasIncludesFees =
    (selectedOption?.totalCustomerFeeAmount?.amount ?? 0) > 0;
  const suppressFeeLineForSubvention = selectedOption
    ? shouldSuppressFeeLineForSubventionBanner(
        subventionBannerMessageKey,
        selectedOption,
      )
    : false;
  const subventionEligibleTenures = useMemo(
    () => getSubventionEligibleTenures(session?.paymentOptions ?? []),
    [session?.paymentOptions],
  );

  const hasInstalmentOptions = session?.paymentOptions?.some(
    (option) => option.tenure >= 2,
  );
  const hasMultipleOptions = paymentOptions.length > 1;
  const hasAdjustedDownPayment =
    selectedOption?.equalDownPaymentStrategy === false;
  // Merchandising callout ("Payment plan tailored for you!"). Shown only when
  // ALL hold: the price_callout_ppui Statsig gate is on; the selected option is an
  // equal (non-adjusted) plan carrying the LOWEST_MARKET_PRICE trait; and the
  // plan is NOT subvented (subvention overrides the callout). Declined handling
  // is applied at render. The gate value is false until Statsig is ready, so an
  // off/absent gate never renders the callout.
  const hasLowestMarketPrice = Boolean(
    priceCalloutGate.value &&
      !selectedIsSubventionEligible &&
      selectedOption?.equalDownPaymentStrategy === true &&
      selectedOption?.traits?.includes(PaymentOptionTrait.LOWEST_MARKET_PRICE),
  );
  const downPaymentAmount = selectedOption?.downPaymentAmount?.amount ?? 0;
  const feeAmount = selectedOption?.totalCustomerFeeAmount?.amount ?? 0;
  const averageMonthlyFee = selectedOption?.averageMonthlyFee;
  const currency = session?.order?.currency ?? Currency.SAR;

  const isSelectedOptionDeclined =
    paymentOptions.find((o) => o.payments === selectedPaymentsForDisplay)
      ?.isDeclined ?? false;

  useEffect(() => {
    if (isSelectedOptionDeclined) {
      logInfo("instalment-option-picker", "Declined offer UI shown", {
        selectedInstalments: selectedPaymentsForDisplay,
        notAvailableTextShown: true,
        lockIconInSelectorShown: true,
        lockIconInBadgeShown: true,
        ineligibleMessageShown: true,
        planDetailsDisabled: true,
        continueButtonDisabled: true,
      });
    }
  }, [isSelectedOptionDeclined, selectedPaymentsForDisplay]);

  const handleSeeDetailsClick = () => {
    if (disabled) return;
    ctx.sendEvent(events.SEE_PAYMENT_PLAN_DETAILS_CLICKED, {
      instalments: selectedPaymentsForDisplay,
    });
    if (selectedOption && session) {
      emitPaymentDetailsBottomSheetTracking(
        "payment__payment_details_clicked",
        selectedOption,
        session,
      );
    }
    setIsOrderDetailsOpen(true);
  };

  const handlePaymentSelectionChange = (payments: number) => {
    ctx.sendEvent(events.SELECTED_PAYMENT_OPTION, {
      instalments: payments,
    });

    const clickedOption = paymentOptions.find((o) => o.payments === payments);
    if (clickedOption?.isDeclined && session) {
      logInfo(
        "instalment-option-picker",
        "Customer clicked locked (declined) option",
        {
          instalments: payments,
        },
      );
    }

    const visualIndex = paymentOptions.findIndex(
      (option) => option.payments === payments,
    );

    setSelectedPaymentOption({
      payments,
      type: "PAY_BY_INSTALMENTS",
      visualIndex: visualIndex >= 0 ? visualIndex : 0,
    });

    const newSelectedOption = session?.paymentOptions?.find(
      (option) => option.tenure === payments,
    );
    if (newSelectedOption && session) {
      emitPaymentPlanTracking(
        "user_switched_payment_plan",
        newSelectedOption,
        session,
      );
    }
  };

  if (!hasInstalmentOptions) {
    return null;
  }

  return (
    <Card
      className={`mb-8 bg-[#F4F6F9] border-gray-200 transition-opacity ${disabled ? "opacity-50" : ""}`}
    >
      <CardContent className="p-6">
        <PaymentHeader
          hasAdjustedDownPayment={hasAdjustedDownPayment}
          downPaymentAmount={downPaymentAmount}
          monthlyAmount={monthlyAmount}
          currency={currency}
          language={i18n.language}
          selectedPayments={selectedPaymentsForDisplay}
          isDeclinedOfferSelected={isSelectedOptionDeclined}
          t={t}
        />

        {!isSelectedOptionDeclined &&
          selectedOption?.equalDownPaymentStrategy === true &&
          !suppressFeeLineForSubvention && (
            <FeeDisplay
              hasIncludesFees={hasIncludesFees}
              feeAmount={feeAmount}
              averageMonthlyFee={averageMonthlyFee}
              currency={currency}
              language={i18n.language}
              t={t}
            />
          )}

        {!isSelectedOptionDeclined && hasLowestMarketPrice && (
          <div className="flex items-center gap-2.5 px-6 pb-3 text-[#6900A9]">
            <SparkleIcon
              className="w-[22px] h-[22px] shrink-0"
              aria-hidden
              data-testid="lowest-market-price-icon"
            />
            <span className="text-[12px] font-medium leading-[16px] tracking-[-0.18px]">
              {t("plan.lowest_market_price")}
            </span>
          </div>
        )}

        {selectedIsSubventionEligible && subventionBannerMessageKey && (
          <div className="px-6 pb-3">
            <div className="flex items-center gap-2 py-2.5 px-4 rounded-xl border border-solid border-[#128351] bg-[#F0FBF5]">
              <PercentageWhiteCircleIcon
                className="w-4 h-4 shrink-0 [&_path]:fill-[#128351]"
                aria-hidden
              />
              <span className="text-[#128351] text-sm font-medium">
                {t(subventionBannerMessageKey)}
              </span>
            </div>
          </div>
        )}

        {isSelectedOptionDeclined && (
          <div className="mt-6 flex justify-center px-6 pb-3">
            <div className="flex items-center rounded-xl bg-[#EBEEF4] pt-2.5 pr-4 pb-3 pl-4">
              <p className="text-sm text-[#585B7A]">
                {t("plan.not_eligible_for_split", {
                  number: selectedPaymentsForDisplay,
                })}
              </p>
            </div>
          </div>
        )}

        {hasMultipleOptions && (
          <div className="pt-3 pb-4 px-6">
            <PaymentOptionSelect
              selectedPayments={selectedPaymentsForDisplay}
              onPaymentSelectionChange={handlePaymentSelectionChange}
              paymentOptions={paymentOptions}
              subventionData={subventionData}
              subventionEligibleTenures={subventionEligibleTenures}
              currency={currency}
              language={i18n.language}
              waivedOffFeeData={waivedOffFeeData}
              disabled={disabled}
            />
          </div>
        )}

        {selectedOption?.equalDownPaymentStrategy === false && (
          <>
            {hasIncludesFees ? (
              <UnequalPlanBannerWithFee
                downPaymentAmount={downPaymentAmount}
                currency={currency}
                language={i18n.language}
                t={t}
                averageMonthlyFee={averageMonthlyFee}
              />
            ) : (
              <UnequalPlanBanner
                downPaymentAmount={downPaymentAmount}
                currency={currency}
                language={i18n.language}
                t={t}
              />
            )}
          </>
        )}

        <SeeDetailsButton
          disabled={disabled || isSelectedOptionDeclined}
          onClick={handleSeeDetailsClick}
          chevronIcon={chevronIcon}
          t={t}
        />
      </CardContent>
    </Card>
  );
}
